import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowLeft, ArrowRight, Plus, Trash2 } from 'lucide-react';
import API_BASE from '../services/apiBase';
import { getAdminPasswordHeaders, ensureAdminApiPassword } from '../services/http';

const emptyPerson = () => ({
  nameWithInitials: '',
  passportNo: '',
  address: '',
  mobiles: [''],
  passportPhoto: null,
});

const emptyConsignee = () => ({
  nameWithInitials: '',
  idType: 'passport',
  passportNo: '',
  nicNo: '',
  drivingLicenceNo: '',
  address: '',
  mobiles: [''],
  passportPhoto: null,
  drivingLicencePhoto: null,
  nicFront: null,
  nicBack: null,
});

const ADMIN_IMAGE_MAX_BYTES = 10 * 1024 * 1024;

function PreviewImage({ file, alt = '', style }) {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (!file) {
      setUrl('');
      return undefined;
    }
    if (typeof file === 'string') {
      let next = file;
      if (!/^https?:\/\//i.test(file)) {
        next = file.startsWith('/') ? `${API_BASE}${file}` : file;
      }
      setUrl(next);
      return undefined;
    }
    let objectUrl = '';
    try {
      objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);
    } catch {
      setUrl('');
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  if (!url) return null;
  return <img src={url} alt={alt} style={style} />;
}

function personPhotoFromApi(data, field) {
  const url = data?.[`${field}Url`] || data?.[field];
  return url && typeof url === 'string' ? url : null;
}

function isDoorToDoor(clearingType) {
  const s = String(clearingType || '').toLowerCase().replace(/\s+/g, '_');
  return s === 'door_to_door' || s === 'd2d';
}

function isConsolidatedParent(collection) {
  return Array.isArray(collection?.childCollectionIds) && collection.childCollectionIds.length > 0;
}

function collectionId(c) {
  return String(c?._id || c?.id || '');
}

function boxCount(collection) {
  const boxes = Array.isArray(collection?.boxes) ? collection.boxes : [];
  return boxes.reduce((sum, b) => {
    const q = Number.parseInt(String(b.quantity), 10);
    return sum + (Number.isFinite(q) && q > 0 ? q : 1);
  }, 0);
}

function buildPersonFormData(type, person) {
  const fd = new FormData();
  fd.append('nameWithInitials', person.nameWithInitials || '');
  fd.append('address', person.address || '');
  (person.mobiles || []).filter(Boolean).forEach((m, i) => fd.append(`mobiles[${i}]`, m));
  if (type === 'shipper') {
    fd.append('passportNo', person.passportNo || '');
    if (person.passportPhoto instanceof File) fd.append('passportPhoto', person.passportPhoto);
  } else {
    fd.append('idType', person.idType || 'passport');
    if (person.idType === 'passport') fd.append('passportNo', person.passportNo || '');
    if (person.idType === 'nic') fd.append('nicNo', person.nicNo || '');
    if (person.idType === 'driving_licence') fd.append('drivingLicenceNo', person.drivingLicenceNo || '');
    if (person.passportPhoto instanceof File) fd.append('passportPhoto', person.passportPhoto);
    if (person.drivingLicencePhoto instanceof File) fd.append('drivingLicencePhoto', person.drivingLicencePhoto);
    if (person.nicFront instanceof File) fd.append('nicFront', person.nicFront);
    if (person.nicBack instanceof File) fd.append('nicBack', person.nicBack);
  }
  return fd;
}

async function upsertPerson(type, payload) {
  const res = await fetch(`${API_BASE}/api/${type}s/upsert`, {
    method: 'POST',
    body: payload,
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`${type} save failed: ${res.status} - ${errorText}`);
  }
  return res.json();
}

function resolveConsigneeId(consignee) {
  if (consignee.idType === 'nic') return consignee.nicNo;
  if (consignee.idType === 'driving_licence') return consignee.drivingLicenceNo;
  return consignee.passportNo;
}

export default function AdminConsolidateWizard({
  collections = [],
  mode = 'create',
  parentCollection = null,
  onClose,
  onSuccess,
  loadCollectionPeople,
}) {
  const isEdit = mode === 'edit' && parentCollection;
  const [step, setStep] = useState(isEdit ? 2 : 1);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => {
    if (!isEdit) return [];
    return (parentCollection.childCollectionIds || []).map(String);
  });
  const [pendingAddIds, setPendingAddIds] = useState([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [searchingShipper, setSearchingShipper] = useState(false);
  const [searchingConsignee, setSearchingConsignee] = useState(false);
  const [form, setForm] = useState(() => ({
    collectionNo: isEdit ? parentCollection.collectionNo || '' : '',
    collectionDate: isEdit
      ? (parentCollection.collectionDate || '').toString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    clearingType: isEdit ? parentCollection.clearingType || 'upb' : 'upb',
    shipper: emptyPerson(),
    consignee: emptyConsignee(),
  }));

  const collectionsById = useMemo(() => {
    const m = new Map();
    collections.forEach((c) => {
      const id = collectionId(c);
      if (id) m.set(id, c);
    });
    return m;
  }, [collections]);

  const availableD2d = useMemo(() => {
    const parentId = isEdit ? collectionId(parentCollection) : '';
    const q = search.trim().toLowerCase();
    return collections
      .filter((c) => {
        const id = collectionId(c);
        if (!isDoorToDoor(c.clearingType)) return false;
        const lockedInto = c.consolidatedInto ? String(c.consolidatedInto) : '';
        if (lockedInto && lockedInto !== parentId) return false;
        if (isEdit && selectedIds.includes(id) && !pendingRemoveIds.includes(id)) return false;
        if (!isEdit && lockedInto) return false;
        if (!q) return true;
        const blob = [c.collectionNo, c.consigneeId, c.shipperPassportNo, id].join(' ').toLowerCase();
        return blob.includes(q);
      })
      .sort((a, b) => String(a.collectionNo || '').localeCompare(String(b.collectionNo || ''), undefined, { numeric: true }));
  }, [collections, search, isEdit, parentCollection, selectedIds, pendingRemoveIds]);

  const linkedRows = useMemo(() => {
    return selectedIds
      .map((id) => collectionsById.get(id))
      .filter(Boolean);
  }, [selectedIds, collectionsById]);

  useEffect(() => {
    if (!isEdit || !loadCollectionPeople) return;
    let cancelled = false;
    (async () => {
      try {
        const { shipperData, consigneeData } = await loadCollectionPeople(parentCollection);
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          shipper: {
            ...prev.shipper,
            nameWithInitials: shipperData?.nameWithInitials || '',
            passportNo: shipperData?.passportNo || parentCollection.shipperPassportNo || '',
            address: shipperData?.address || '',
            mobiles: Array.isArray(shipperData?.mobiles) && shipperData.mobiles.length
              ? shipperData.mobiles
              : [''],
            passportPhoto: personPhotoFromApi(shipperData, 'passportPhoto'),
          },
          consignee: {
            ...prev.consignee,
            nameWithInitials: consigneeData?.nameWithInitials || '',
            idType: consigneeData?.idType || (consigneeData?.nicNo ? 'nic' : 'passport'),
            passportNo: consigneeData?.passportNo || '',
            nicNo: consigneeData?.nicNo || '',
            drivingLicenceNo: consigneeData?.drivingLicenceNo || '',
            address: consigneeData?.address || '',
            mobiles: Array.isArray(consigneeData?.mobiles) && consigneeData.mobiles.length
              ? consigneeData.mobiles
              : [''],
            passportPhoto: personPhotoFromApi(consigneeData, 'passportPhoto'),
            drivingLicencePhoto: personPhotoFromApi(consigneeData, 'drivingLicencePhoto'),
            nicFront: personPhotoFromApi(consigneeData, 'nicFront'),
            nicBack: personPhotoFromApi(consigneeData, 'nicBack'),
          },
        }));
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [isEdit, parentCollection, loadCollectionPeople]);

  const updateField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      if (parts.length === 1) {
        next[parts[0]] = value;
        return next;
      }
      const [section, field] = parts;
      next[section] = { ...next[section], [field]: value };
      return next;
    });
  };

  const updateMobile = (section, idx, value) => {
    setForm((prev) => {
      const mobiles = [...prev[section].mobiles];
      mobiles[idx] = value;
      return {
        ...prev,
        [section]: { ...prev[section], mobiles },
      };
    });
  };

  const addMobile = (section) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        mobiles: [...prev[section].mobiles, ''],
      },
    }));
  };

  const handlePhotoFile = (path, file) => {
    if (!file) return;
    if (file.size > ADMIN_IMAGE_MAX_BYTES) {
      alert('Image must be 10MB or smaller.');
      return;
    }
    updateField(path, file);
  };

  const renderPhotoField = (label, path, photoValue, { fullWidth = true } = {}) => (
    <div style={fullWidth ? { gridColumn: '1 / -1' } : undefined}>
      <label className="form-label">{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ width: '80px', height: '80px', border: '1px dashed #e5e7eb', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
          {photoValue ? (
            <PreviewImage file={photoValue} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>No Image</span>
          )}
        </div>
        <label className="jet-btn jet-btn-secondary" style={{ cursor: 'pointer' }}>
          Choose File
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handlePhotoFile(path, e.target.files?.[0])}
            style={{ display: 'none' }}
          />
        </label>
      </div>
    </div>
  );

  const searchShipper = async (passportNo) => {
    if (!String(passportNo || '').trim()) {
      alert('Please enter a passport number to search');
      return;
    }

    setSearchingShipper(true);
    try {
      const res = await fetch(`${API_BASE}/api/shippers/by-passport/${encodeURIComponent(passportNo.trim())}`);
      if (res.ok) {
        const shipperData = await res.json();
        if (shipperData) {
          setForm((prev) => ({
            ...prev,
            shipper: {
              ...prev.shipper,
              nameWithInitials: shipperData.nameWithInitials || '',
              passportNo: shipperData.passportNo || passportNo.trim(),
              address: shipperData.address || '',
              mobiles: shipperData.mobiles?.length ? shipperData.mobiles : [''],
              passportPhoto: personPhotoFromApi(shipperData, 'passportPhoto'),
            },
          }));
          alert('Shipper data auto-filled successfully!');
        } else {
          alert('No shipper found with this passport number');
        }
      } else if (res.status === 404) {
        alert('No shipper found with this passport number');
      } else {
        const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
        alert('Error searching for shipper: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Shipper search error:', error);
      alert('Failed to search for shipper: ' + error.message);
    } finally {
      setSearchingShipper(false);
    }
  };

  const searchConsignee = async (idNumber) => {
    if (!String(idNumber || '').trim()) {
      alert('Please enter an ID number to search');
      return;
    }

    setSearchingConsignee(true);
    try {
      const trimmed = idNumber.trim();
      const idType = form.consignee.idType;
      let res;
      let consigneeData = null;

      if (idType === 'nic') {
        res = await fetch(`${API_BASE}/api/consignees/by-nic/${encodeURIComponent(trimmed)}`);
        if (res.ok) consigneeData = await res.json();
      } else if (idType === 'driving_licence') {
        res = await fetch(`${API_BASE}/api/consignees/by-driving-licence/${encodeURIComponent(trimmed)}`);
        if (res.ok) consigneeData = await res.json();
      } else {
        res = await fetch(`${API_BASE}/api/consignees/by-passport/${encodeURIComponent(trimmed)}`);
        if (res.ok) {
          consigneeData = await res.json();
        } else if (res.status === 404) {
          res = await fetch(`${API_BASE}/api/consignees/by-nic/${encodeURIComponent(trimmed)}`);
          if (res.ok) consigneeData = await res.json();
        } else {
          const errorData = await res.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || 'Unknown error');
        }
      }

      if (consigneeData) {
        setForm((prev) => ({
          ...prev,
          consignee: {
            ...prev.consignee,
            nameWithInitials: consigneeData.nameWithInitials || '',
            idType: consigneeData.idType || prev.consignee.idType || 'passport',
            passportNo: consigneeData.passportNo || '',
            nicNo: consigneeData.nicNo || '',
            drivingLicenceNo: consigneeData.drivingLicenceNo || '',
            address: consigneeData.address || '',
            mobiles: consigneeData.mobiles?.length ? consigneeData.mobiles : [''],
            passportPhoto: personPhotoFromApi(consigneeData, 'passportPhoto'),
            drivingLicencePhoto: personPhotoFromApi(consigneeData, 'drivingLicencePhoto'),
            nicFront: personPhotoFromApi(consigneeData, 'nicFront'),
            nicBack: personPhotoFromApi(consigneeData, 'nicBack'),
          },
        }));
        alert('Consignee data auto-filled successfully!');
      } else if (res?.status === 404) {
        alert('No consignee found with this ID number');
      } else {
        alert('No consignee found with this ID number');
      }
    } catch (error) {
      console.error('Consignee search error:', error);
      alert('Failed to search for consignee: ' + error.message);
    } finally {
      setSearchingConsignee(false);
    }
  };

  const toggleSelect = (id) => {
    const sid = String(id);
    if (isEdit) {
      setPendingAddIds((prev) => (
        prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
      ));
      return;
    }
    setSelectedIds((prev) => (
      prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
    ));
  };

  const removeLinked = (id) => {
    const sid = String(id);
    if (isEdit) {
      if (linkedRows.length - pendingRemoveIds.length + pendingAddIds.length <= 1) {
        alert('At least one door-to-door bill must remain linked.');
        return;
      }
      setPendingRemoveIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]));
      return;
    }
    setSelectedIds((prev) => prev.filter((x) => x !== sid));
  };

  const undoRemove = (id) => {
    setPendingRemoveIds((prev) => prev.filter((x) => x !== String(id)));
  };

  const addPendingToLinked = () => {
    if (!pendingAddIds.length) return;
    setSelectedIds((prev) => [...new Set([...prev, ...pendingAddIds])]);
    setPendingAddIds([]);
  };

  const canGoNext = isEdit || selectedIds.length > 0;

  const handleSubmit = async () => {
    if (!form.collectionNo?.trim()) {
      alert('Collection No is required.');
      return;
    }
    if (!form.clearingType) {
      alert('Please select UPB or Gift.');
      return;
    }
    if (!['upb', 'gift'].includes(String(form.clearingType).toLowerCase())) {
      alert('Clearing type must be UPB or Gift.');
      return;
    }
    if (!form.consignee.idType) {
      alert('Please select consignee ID type.');
      return;
    }

    const effectiveIds = isEdit
      ? selectedIds.filter((id) => !pendingRemoveIds.includes(id)).concat(pendingAddIds)
      : selectedIds;

    if (!effectiveIds.length) {
      alert('Select at least one door-to-door bill.');
      return;
    }

    setBusy(true);
    try {
      await Promise.all([
        upsertPerson('shipper', buildPersonFormData('shipper', form.shipper)),
        upsertPerson('consignee', buildPersonFormData('consignee', form.consignee)),
      ]);

      const adminPwd = ensureAdminApiPassword(
        isEdit ? 'Enter admin password to update consolidated bill:' : 'Enter admin password to create consolidated bill:',
      );
      if (!adminPwd) {
        setBusy(false);
        return;
      }

      const payload = {
        collectionNo: form.collectionNo.trim(),
        collectionDate: form.collectionDate,
        clearingType: form.clearingType,
        shipper: form.shipper,
        consignee: form.consignee,
        shipperPassportNo: form.shipper.passportNo,
        consigneeId: resolveConsigneeId(form.consignee),
      };

      let res;
      if (isEdit) {
        res = await fetch(`${API_BASE}/api/collections/${collectionId(parentCollection)}/consolidate`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAdminPasswordHeaders(adminPwd),
          },
          body: JSON.stringify({
            ...payload,
            addChildCollectionIds: pendingAddIds,
            removeChildCollectionIds: pendingRemoveIds,
          }),
        });
      } else {
        res = await fetch(`${API_BASE}/api/collections/consolidate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAdminPasswordHeaders(adminPwd),
          },
          body: JSON.stringify({
            ...payload,
            childCollectionIds: selectedIds,
          }),
        });
      }

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || `Request failed (${res.status})`);
      }

      const saved = await res.json();
      onSuccess?.(saved);
      onClose?.();
    } catch (e) {
      console.error(e);
      alert(e.message || 'Failed to save consolidated bill');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="jet-admin-modal-overlay jet-admin-modal-overlay--dark">
      <div className="jet-admin-modal-panel" style={{ maxWidth: '960px', width: 'min(96vw, 960px)' }}>
        <div className="jet-admin-modal-panel__body">
          <div className="jet-admin-modal-header">
            <div>
              <h2 style={{ margin: 0 }}>
                {isEdit ? `Edit consolidated bill ${parentCollection.collectionNo}` : 'Door to Door → UPB/Gift'}
              </h2>
              <p style={{ margin: '0.35rem 0 0', color: 'var(--jet-muted)', fontSize: '0.85rem' }}>
                {step === 1
                  ? 'Select door-to-door bills to include in the consolidated customs bill.'
                  : 'Enter the new UPB/Gift customer details for customs declaration.'}
              </p>
            </div>
            <button type="button" className="jet-btn jet-btn-secondary jet-btn-icon" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>

          {step === 1 && !isEdit && (
            <>
              <input
                className="form-input"
                placeholder="Search door-to-door bills…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: '0.75rem' }}
              />
              <div className="jet-table-wrap" style={{ maxHeight: '320px', overflow: 'auto', marginBottom: '1rem' }}>
                <table className="jet-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th>Bill No</th>
                      <th>Consignee</th>
                      <th>PCS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableD2d.length === 0 ? (
                      <tr><td colSpan={4} style={{ color: 'var(--jet-muted)' }}>No available door-to-door bills.</td></tr>
                    ) : availableD2d.map((c) => {
                      const id = collectionId(c);
                      const checked = selectedIds.includes(id);
                      return (
                        <tr key={id}>
                          <td>
                            <input type="checkbox" checked={checked} onChange={() => toggleSelect(id)} />
                          </td>
                          <td style={{ fontWeight: 600 }}>{c.collectionNo}</td>
                          <td>{c.consigneeId || '—'}</td>
                          <td>{boxCount(c)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {selectedIds.length > 0 && (
                <p style={{ fontSize: '0.85rem', color: 'var(--jet-muted)' }}>
                  Selected: {selectedIds.map((id) => collectionsById.get(id)?.collectionNo || id).join(', ')}
                </p>
              )}
            </>
          )}

          {step === 2 && (
            <>
              <div className="jet-surface-muted" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <strong>Linked door-to-door bills ({linkedRows.filter((c) => !pendingRemoveIds.includes(collectionId(c))).length})</strong>
                  {!isEdit ? (
                    <button type="button" className="jet-btn jet-btn-secondary" onClick={() => setStep(1)}>
                      Change selection
                    </button>
                  ) : null}
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {linkedRows.map((c) => {
                    const id = collectionId(c);
                    const removed = pendingRemoveIds.includes(id);
                    return (
                      <div key={id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: removed ? 0.5 : 1 }}>
                        <span>{c.collectionNo} · {boxCount(c)} PCS · {c.consigneeId || '—'}</span>
                        {removed ? (
                          <button type="button" className="jet-btn jet-btn-secondary" style={{ fontSize: '0.75rem' }} onClick={() => undoRemove(id)}>Undo remove</button>
                        ) : (
                          <button type="button" className="jet-btn jet-btn-danger jet-btn-icon" onClick={() => removeLinked(id)} title="Remove">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {isEdit && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <strong>Add more door-to-door bills</strong>
                    <button type="button" className="jet-btn jet-btn-secondary" disabled={!pendingAddIds.length} onClick={addPendingToLinked}>
                      <Plus size={14} /> Add selected
                    </button>
                  </div>
                  <input
                    className="form-input"
                    placeholder="Search available D2D bills…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ marginBottom: '0.5rem' }}
                  />
                  <div className="jet-surface-bordered" style={{ marginBottom: '0.5rem' }}>
                    {availableD2d.length === 0 ? (
                      <p style={{ margin: 0, color: 'var(--jet-muted)', fontSize: '0.85rem' }}>No additional door-to-door bills available.</p>
                    ) : availableD2d.map((c) => {
                      const id = collectionId(c);
                      const checked = pendingAddIds.includes(id);
                      return (
                        <label key={id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.25rem 0' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleSelect(id)} />
                          <span>{c.collectionNo} · {c.consigneeId || '—'} · {boxCount(c)} PCS</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-3" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label className="form-label">Collection No.</label>
                  <input
                    className="form-input"
                    value={form.collectionNo}
                    onChange={(e) => updateField('collectionNo', e.target.value)}
                    readOnly={isEdit}
                  />
                </div>
                <div>
                  <label className="form-label">Collection Date</label>
                  <input type="date" className="form-input" value={form.collectionDate || ''} onChange={(e) => updateField('collectionDate', e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Clearing Type</label>
                  <select className="form-input" value={form.clearingType} onChange={(e) => updateField('clearingType', e.target.value)}>
                    <option value="upb">UPB</option>
                    <option value="gift">Gift</option>
                  </select>
                </div>
              </div>

              <h3 style={{ fontWeight: 600, margin: '0 0 0.75rem' }}>Shipper (customs customer)</h3>
              <div className="grid grid-2" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label className="form-label">Name with Initials</label>
                  <input className="form-input" value={form.shipper.nameWithInitials} onChange={(e) => updateField('shipper.nameWithInitials', e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Passport No</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'stretch' }}>
                    <input
                      className="form-input"
                      value={form.shipper.passportNo}
                      onChange={(e) => updateField('shipper.passportNo', e.target.value)}
                      style={{ flex: '1 1 160px', minWidth: 0 }}
                      placeholder="Enter passport number"
                    />
                    <button
                      type="button"
                      className="jet-btn jet-btn-secondary"
                      onClick={() => searchShipper(form.shipper.passportNo)}
                      disabled={searchingShipper || !form.shipper.passportNo.trim()}
                      style={{ minWidth: '80px' }}
                    >
                      {searchingShipper ? '...' : 'Search'}
                    </button>
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Address</label>
                  <textarea className="form-input" rows={2} value={form.shipper.address} onChange={(e) => updateField('shipper.address', e.target.value)} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Mobile Numbers</label>
                  {form.shipper.mobiles.map((m, i) => (
                    <input
                      key={`shipper-mobile-${i}`}
                      className="form-input"
                      style={{ marginBottom: '0.5rem' }}
                      value={m}
                      onChange={(e) => updateMobile('shipper', i, e.target.value)}
                    />
                  ))}
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={() => addMobile('shipper')} style={{ marginTop: '0.25rem' }}>
                    + Add Mobile
                  </button>
                </div>
                {renderPhotoField('Passport Photo', 'shipper.passportPhoto', form.shipper.passportPhoto)}
              </div>

              <h3 style={{ fontWeight: 600, margin: '0 0 0.75rem' }}>Consignee (customs customer)</h3>
              <div className="grid grid-2" style={{ gap: '1rem' }}>
                <div>
                  <label className="form-label">Name with Initials</label>
                  <input className="form-input" value={form.consignee.nameWithInitials} onChange={(e) => updateField('consignee.nameWithInitials', e.target.value)} />
                </div>
                <div>
                  <label className="form-label">ID Type</label>
                  <select className="form-input" value={form.consignee.idType} onChange={(e) => updateField('consignee.idType', e.target.value)}>
                    <option value="passport">Passport</option>
                    <option value="nic">NIC</option>
                    <option value="driving_licence">Driving Licence</option>
                  </select>
                </div>
                {form.consignee.idType === 'passport' && (
                  <div>
                    <label className="form-label">Passport No</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'stretch' }}>
                      <input
                        className="form-input"
                        value={form.consignee.passportNo}
                        onChange={(e) => updateField('consignee.passportNo', e.target.value)}
                        style={{ flex: '1 1 160px', minWidth: 0 }}
                        placeholder="Enter passport number"
                      />
                      <button
                        type="button"
                        className="jet-btn jet-btn-secondary"
                        onClick={() => searchConsignee(form.consignee.passportNo)}
                        disabled={searchingConsignee || !form.consignee.passportNo.trim()}
                        style={{ minWidth: '80px' }}
                      >
                        {searchingConsignee ? '...' : 'Search'}
                      </button>
                    </div>
                  </div>
                )}
                {form.consignee.idType === 'nic' && (
                  <div>
                    <label className="form-label">NIC No</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'stretch' }}>
                      <input
                        className="form-input"
                        value={form.consignee.nicNo}
                        onChange={(e) => updateField('consignee.nicNo', e.target.value)}
                        style={{ flex: '1 1 160px', minWidth: 0 }}
                        placeholder="Enter NIC number"
                      />
                      <button
                        type="button"
                        className="jet-btn jet-btn-secondary"
                        onClick={() => searchConsignee(form.consignee.nicNo)}
                        disabled={searchingConsignee || !form.consignee.nicNo.trim()}
                        style={{ minWidth: '80px' }}
                      >
                        {searchingConsignee ? '...' : 'Search'}
                      </button>
                    </div>
                  </div>
                )}
                {form.consignee.idType === 'driving_licence' && (
                  <div>
                    <label className="form-label">Driving Licence No</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'stretch' }}>
                      <input
                        className="form-input"
                        value={form.consignee.drivingLicenceNo}
                        onChange={(e) => updateField('consignee.drivingLicenceNo', e.target.value)}
                        style={{ flex: '1 1 160px', minWidth: 0 }}
                        placeholder="Enter driving licence number"
                      />
                      <button
                        type="button"
                        className="jet-btn jet-btn-secondary"
                        onClick={() => searchConsignee(form.consignee.drivingLicenceNo)}
                        disabled={searchingConsignee || !form.consignee.drivingLicenceNo.trim()}
                        style={{ minWidth: '80px' }}
                      >
                        {searchingConsignee ? '...' : 'Search'}
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Address</label>
                  <textarea className="form-input" rows={2} value={form.consignee.address} onChange={(e) => updateField('consignee.address', e.target.value)} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Mobile Numbers</label>
                  {form.consignee.mobiles.map((m, i) => (
                    <input
                      key={`consignee-mobile-${i}`}
                      className="form-input"
                      style={{ marginBottom: '0.5rem' }}
                      value={m}
                      onChange={(e) => updateMobile('consignee', i, e.target.value)}
                    />
                  ))}
                  <button type="button" className="jet-btn jet-btn-secondary" onClick={() => addMobile('consignee')} style={{ marginTop: '0.25rem' }}>
                    + Add Mobile
                  </button>
                </div>
                {form.consignee.idType === 'passport' && renderPhotoField('Passport Photo', 'consignee.passportPhoto', form.consignee.passportPhoto)}
                {form.consignee.idType === 'nic' && (
                  <div className="grid grid-2" style={{ gap: '1rem', gridColumn: '1 / -1' }}>
                    {renderPhotoField('NIC Front', 'consignee.nicFront', form.consignee.nicFront, { fullWidth: false })}
                    {renderPhotoField('NIC Back', 'consignee.nicBack', form.consignee.nicBack, { fullWidth: false })}
                  </div>
                )}
                {form.consignee.idType === 'driving_licence' && renderPhotoField('Driving Licence Photo', 'consignee.drivingLicencePhoto', form.consignee.drivingLicencePhoto)}
              </div>
            </>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.25rem', gap: '0.5rem', flexWrap: 'wrap' }}>
            <div>
              {step === 2 && !isEdit ? (
                <button type="button" className="jet-btn jet-btn-secondary" onClick={() => setStep(1)}>
                  <ArrowLeft size={16} /> Back
                </button>
              ) : null}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" className="jet-btn jet-btn-secondary" onClick={onClose}>Cancel</button>
              {step === 1 && !isEdit ? (
                <button type="button" className="jet-btn jet-btn-primary" disabled={!canGoNext} onClick={() => setStep(2)}>
                  Next <ArrowRight size={16} />
                </button>
              ) : (
                <button type="button" className="jet-btn jet-btn-primary" disabled={busy} onClick={handleSubmit}>
                  {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create consolidated bill'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { isConsolidatedParent, isDoorToDoor };
